/**
 * Phase 30.3: scheduleTaskUpsert — debounced, serialized task writes.
 *
 * Problem
 * -------
 * `upsertTasks` sends the FULL task list for a project as a single bulk
 * upsert. The cascade scheduler triggers an upsert on every task edit, and
 * rapid edits cause overlapping in-flight upserts that touch the same rows
 * — Postgres detects a deadlock (40P01) and kills one transaction. The user
 * sees an error toast even though their edits were valid.
 *
 * Fix (B + A)
 * -----------
 *   B. DEBOUNCE — coalesce rapid calls into one save. Each call replaces the
 *      pending payload for that project and resets the timer; only the LAST
 *      payload in a quick burst actually goes to the server. Because every
 *      call carries the full task list, the latest payload already includes
 *      all prior edits, so coalescing is lossless.
 *
 *   A. MUTEX — even after debouncing, two requests for the same project can
 *      still arrive back-to-back (e.g. a programmatic call right after a
 *      debounced one fired). A per-project lock guarantees the next request
 *      waits for the previous to complete before starting.
 *
 * Together: deadlocks are eliminated both by reducing overlapping requests
 * (debounce) and by hard-serializing whatever still gets through (mutex).
 *
 * Failure surfacing matches the prior fireApi behaviour — any error from the
 * actual upsert produces the same "X: <msg>. Refresh to re-sync." toast.
 *
 * Scope: tasks only. The cascade-driven bulk-upsert pattern is unique to the
 * tasks code path. Other tables (change_requests, risks, etc.) don't have
 * the same overlapping-write behaviour.
 */

import type { Task } from '@/types';
import { upsertTasks } from '@/lib/api';
import { toast } from '@/store/useToastStore';

const DEBOUNCE_MS = 300;

/**
 * Per-project pending write. The most recent call's data replaces any prior
 * pending payload for that project.
 */
interface Pending {
  tasks: Task[];
  label: string;
  timer: ReturnType<typeof setTimeout>;
}

const pending = new Map<string, Pending>();

/**
 * Per-project in-flight promise. When a flush starts, the promise is stored
 * here; the next flush awaits it before opening its own request. This is the
 * mutex.
 */
const inflight = new Map<string, Promise<void>>();

/**
 * Schedule a task upsert. Returns immediately. The actual server call
 * happens after DEBOUNCE_MS of quiet, serialized per project.
 *
 * The `label` is used only for the error toast (e.g. "Add task",
 * "Update tasks") — it mirrors the previous fireApi label.
 */
export function scheduleTaskUpsert(
  projectId: string,
  tasks: Task[],
  label: string,
): void {
  // If a flush is already pending for this project, cancel its timer; we
  // replace the payload with the latest one.
  const existing = pending.get(projectId);
  if (existing) clearTimeout(existing.timer);

  const timer = setTimeout(() => {
    void flushProject(projectId);
  }, DEBOUNCE_MS);

  pending.set(projectId, {
    // Snapshot the array — the caller may continue mutating its reference.
    tasks: tasks.slice(),
    label,
    timer,
  });
}

/**
 * Send the pending payload for a project, respecting the per-project mutex.
 * If a previous flush for this project is still in flight, queue behind it.
 */
async function flushProject(projectId: string): Promise<void> {
  const entry = pending.get(projectId);
  if (!entry) return;
  pending.delete(projectId);
  const { tasks, label } = entry;

  // Mutex: chain onto any in-flight write for this project.
  const prior = inflight.get(projectId) ?? Promise.resolve();

  const run = prior
    .catch(() => {
      // A prior write's error already surfaced via toast; don't block this
      // write on it. Swallow here so the chain continues.
    })
    .then(async () => {
      try {
        const res = await upsertTasks(projectId, tasks);
        if (res.error) {
          toast.error(`${label}: ${res.error}. Refresh to re-sync.`, 6000);
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        toast.error(`${label}: ${msg}. Refresh to re-sync.`, 6000);
      }
    });

  inflight.set(projectId, run);
  try {
    await run;
  } finally {
    // Only clear if no later flush has replaced us as the in-flight write.
    if (inflight.get(projectId) === run) inflight.delete(projectId);
  }
}

/**
 * Flush all pending task writes immediately. Useful before a page unload
 * or workspace switch so debounced edits aren't lost. Currently unused but
 * exported for future wiring.
 */
export async function flushAllPendingTaskWrites(): Promise<void> {
  const projectIds = Array.from(pending.keys());
  for (const id of projectIds) {
    const entry = pending.get(id);
    if (entry) clearTimeout(entry.timer);
  }
  await Promise.all(projectIds.map((id) => flushProject(id)));
}
